/* eslint-disable max-lines */
import { Link } from '@tanstack/react-router';
import { type ColumnDef, createColumnHelper } from '@tanstack/table-core';
import { Info } from 'lucide-react';
import { FormattedMessage } from 'react-intl';
import { Fragment } from 'react/jsx-runtime';

import { TableHeader } from '@/components/DataTable/TableHeader';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { S3Bucket } from '@/lib/queries/integrations/terraformBackend/buckets';

const columnHelper = createColumnHelper<S3Bucket>();

type BucketStatus =
  | 'unsynced-with-cloud'
  | 'synced'
  | 'active'
  | 'no-creds'
  | 'error';

const getStatus = (bucket: S3Bucket): BucketStatus => {
  if (bucket.syncStatus === 'unsynced-with-cloud') {
    return 'unsynced-with-cloud';
  }

  if (bucket.accessStatus === 'active') {
    return 'active';
  }

  if (bucket.accessStatus === 'no-creds') {
    return 'no-creds';
  }

  if (bucket.accessStatus === 'error') {
    return 'error';
  }

  if (bucket.syncStatus === 'unsynced-with-code') {
    throw new Error('This bucket should not be here');
  }

  return 'synced';
};

export const getColumns = (): ColumnDef<S3Bucket>[] =>
  [
    columnHelper.accessor('name', {
      header: ({ column }) => (
        <TableHeader
          column={column}
          headersTranslationPath="integrations.terraformBackend.buckets.list.table"
          type="sort-only"
        />
      ),
      size: 45,
    }),
    columnHelper.accessor('region', {
      header: ({ column }) => (
        <TableHeader
          column={column}
          headersTranslationPath="integrations.terraformBackend.buckets.list.table"
          type="sort-only"
        />
      ),
      size: 20,
    }),
    columnHelper.accessor(getStatus, {
      id: 'status',
      header: ({ column }) => (
        <TableHeader
          column={column}
          type="select"
          headersTranslationPath="integrations.terraformBackend.buckets.list.table"
          optionsTranslationPath="integrations.terraformBackend.buckets.list.table.status.options"
        />
      ),
      cell: ({ getValue, row: { original } }) => {
        const status = getValue();

        switch (status) {
          case 'unsynced-with-cloud':
            return (
              <TooltipProvider>
                <Tooltip delayDuration={100}>
                  <TooltipTrigger asChild>
                    <p>
                      🟠{' '}
                      <Link
                        to="/integrations/aws"
                        className="underline hover:text-blue-500"
                      >
                        Update your AWS credentials
                      </Link>
                      <Info className="inline h-3 w-3 text-gray-500 ml-1" />
                    </p>
                    {/* <p className="underline decoration-dotted">
                      <FormattedMessage id="integrations.terraformBackend.buckets.table.status.options.unsynced-with-cloud" />
                    </p> */}
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-[400px]">
                    <FormattedMessage
                      id="integrations.terraformBackend.buckets.list.table.status.unsynced-with-cloud"
                      values={{
                        br: <br />,
                      }}
                    />
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            );
          case 'synced':
            return (
              <FormattedMessage id="integrations.terraformBackend.buckets.list.table.status.options.synced" />
            );
          case 'active':
            return (
              <FormattedMessage id="integrations.terraformBackend.buckets.list.table.status.options.active" />
            );
          case 'no-creds':
            return (
              <TooltipProvider>
                <Tooltip delayDuration={100}>
                  <TooltipTrigger asChild>
                    <p>
                      🔴{' '}
                      <Link
                        to="/integrations/aws"
                        className="underline hover:text-blue-500"
                      >
                        Update your AWS credentials
                      </Link>
                      <Info className="inline h-3 w-3 text-gray-500 ml-1" />
                    </p>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-[400px]">
                    <FormattedMessage
                      id="integrations.terraformBackend.buckets.list.table.status.no-creds"
                      values={{
                        br: <br />,
                      }}
                    />
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            );
          case 'error':
            return (
              <TooltipProvider>
                <Tooltip delayDuration={100}>
                  <TooltipTrigger asChild>
                    <p>
                      🔴{' '}
                      <Link
                        to="/integrations/aws"
                        className="underline hover:text-blue-500"
                      >
                        Update your AWS credentials
                      </Link>
                      <Info className="inline h-3 w-3 text-gray-500 ml-1" />
                    </p>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-[400px]">
                    <FormattedMessage
                      id="integrations.terraformBackend.buckets.list.table.status.error"
                      values={{
                        creds: (
                          <>
                            <br />
                            {original.errors?.map(({ id, type, name }) => (
                              <Fragment key={id}>
                                •&nbsp;
                                {type === 'role' ? (
                                  <Link
                                    to="/integrations/aws/roles/$roleId"
                                    params={{
                                      roleId: id,
                                    }}
                                    className="underline hover:text-blue-500"
                                  >
                                    {name}
                                  </Link>
                                ) : (
                                  <Link
                                    to="/integrations/aws/roles/$roleId"
                                    params={{
                                      roleId: id,
                                    }}
                                  >
                                    {name}
                                  </Link>
                                )}
                                <br />
                              </Fragment>
                            ))}
                          </>
                        ),
                        br: <br />,
                      }}
                    />
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            );
        }
      },
      size: 35,
    }),
  ] as ColumnDef<S3Bucket>[];
